import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

const normalizeSignupSource = (signupContext) => {
  if (!signupContext || typeof signupContext !== 'object') {
    return null;
  }

  const allowedTypes = ['vendor_storefront', 'property_link'];
  const sourceType = allowedTypes.includes(signupContext.sourceType)
    ? signupContext.sourceType
    : null;

  if (!sourceType) {
    return null;
  }

  const source = {
    sourceType,
    capturedAt: new Date(),
  };

  if (signupContext.vendorId) {
    source.vendorId = String(signupContext.vendorId);
  }
  if (signupContext.propertyId) {
    source.propertyId = String(signupContext.propertyId);
  }

  return source;
};

const buildDefaultProfile = (user, userType = 'investor', signupContext = null) => {
  const normalizedType = (userType || 'investor').toString().toLowerCase();
  const isVendor = normalizedType === 'vendor' || normalizedType === 'wholesaler';
  const signupSource = !isVendor ? normalizeSignupSource(signupContext) : null;

  const baseProfile = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || null,
    userType: isVendor ? 'vendor' : 'investor',
    createdAt: new Date(),
    ...(signupSource ? { accountStatus: 'pending', signupSource } : {}),
  };

  if (isVendor) {
    return baseProfile;
  }

  return {
    ...baseProfile,
    preferences: {
      investmentMin: 100000,
      investmentMax: 2000000,
      preferredLocations: ['San Francisco', 'Oakland', 'San Jose'],
      propertyTypes: ['Single Family', 'Condo', 'Multi-Family']
    },
    stats: {
      propertiesViewed: 0,
      favoritesCount: 0,
      offersMade: 0,
      dealsClosed: 0
    }
  };
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up with email and password
  async function signup(email, password, displayName, userType = 'investor', signupContext = null) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    await updateProfile(user, { displayName });

    // Create initial Firestore profile so backend/vendor checks work immediately
    const profile = buildDefaultProfile(
      { ...user, displayName: displayName || user.displayName },
      userType,
      signupContext
    );
    await setDoc(doc(db, 'users', user.uid), profile, { merge: true });

    return userCredential;
  }

  // Sign in with email and password
  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // The redirect will happen automatically via the useEffect that watches userProfile
    return result;
  }

  // Sign in with Google
  async function loginWithGoogle(userType = 'investor') {
    try {
      const provider = new GoogleAuthProvider();

      // Configure Google provider
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);

      // Ensure profile exists (do not overwrite existing userType)
      const user = result.user;
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        const profile = buildDefaultProfile(user, userType);
        await setDoc(userRef, profile, { merge: true });
      }

      return result;
    } catch (error) {
      console.error('Google sign-in error:', error);

      // Handle specific emulator limitations
      if (error.code === 'auth/operation-not-supported-in-this-environment') {
        console.warn('Google sign-in not supported in emulator. Use email/password for local development.');
        throw new Error('Google sign-in is not available in the local development environment. Please use email/password sign-in for testing, or deploy to production for full Google authentication.');
      }

      throw error;
    }
  }

  // Sign out
  function logout() {
    return signOut(auth);
  }

  // Update user profile
  async function updateUserProfile(updates) {
    try {
      // Update in Firebase
      await setDoc(doc(db, 'users', currentUser.uid), {
        ...updates,
        updatedAt: new Date()
      }, { merge: true });

      // Update local state
      setUserProfile((prev) => ({ ...(prev || {}), ...updates }));
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);

        try {
          // Try to fetch user profile from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));

          if (userDoc.exists()) {
            // Use Firestore data
            setUserProfile({
              uid: user.uid,
              ...userDoc.data()
            });
          } else {
            // Create default profile if none exists
            const defaultProfile = buildDefaultProfile(user, 'investor');

            // Save default profile to Firestore
            await setDoc(doc(db, 'users', user.uid), defaultProfile);
            setUserProfile(defaultProfile);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Fallback to basic profile
          setUserProfile({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || null,
            userType: 'investor'
          });
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    updateUserProfile,
    isInvestor: userProfile?.userType === 'investor',
    isWholesaler: userProfile?.userType === 'wholesaler',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
